/**
 * Chart.js 按需加载工具
 * 
 * 已检查所有现有 toy，确保包含所有必要的组件：
 * - 控制器：LineController, ScatterController
 * - 元素：LineElement, PointElement
 * - 坐标轴：LinearScale, LogarithmicScale, CategoryScale
 * - 插件：Tooltip, Legend, Filler, Title
 * - Decimation 插件：Chart.js 4.x 内置，无需单独注册
 * 
 * 使用方式：
 *   import { getChart } from '@/utils/chart-loader'
 *   const Chart = await getChart()
 *   const chart = new Chart(ctx, config)
 */

let Chart = null
let chartLoadPromise = null

/**
 * 按需加载 Chart.js，包含所有 toy 使用的组件
 * 
 * 已检查的 toy 列表：
 * - hydrology: budyko-theory, harmon-equation, manning-equation, swcc-brooks-corey, swcc-van-genuchten
 * - mathematics: example-toy, linear-regression, taylor-series, delaunay-triangulation, fractal-barnsley-fern
 * - physics: lorenz-perturbation, projectile-motion
 * - machine-learning: random-forest-iris
 * 
 * @returns {Promise<typeof Chart>}
 */
async function loadChart() {
  if (Chart) return Chart
  
  if (chartLoadPromise) return chartLoadPromise
  
  chartLoadPromise = (async () => {
    // 按需导入 Chart.js 核心和所有需要的组件
    const {
      Chart: ChartCore,
      // 控制器（Chart Types）
      LineController,
      ScatterController,
      // 元素（Elements）
      LineElement,
      PointElement,
      // 坐标轴（Scales）
      LinearScale,
      LogarithmicScale,
      CategoryScale,
      // 插件（Plugins）
      Tooltip,
      Legend,
      Filler,
      Title
    } = await import('chart.js')
    
    // 动态导入 annotation 插件
    const { default: Annotation } = await import('chartjs-plugin-annotation')
    
    // 注册所有实际使用的组件
    // 注意：Decimation 插件在 Chart.js 4.x 中是内置的，无需单独注册
    ChartCore.register(
      // 控制器
      LineController,
      ScatterController,
      // 元素
      LineElement,
      PointElement,
      // 坐标轴
      LinearScale,
      LogarithmicScale,
      CategoryScale,
      // 插件
      Tooltip,
      Legend,
      Filler,
      Title,
      Annotation
    )
    
    Chart = ChartCore
    return Chart
  })()
  
  return chartLoadPromise
}

/**
 * 获取 Chart 类（按需加载）
 * @returns {Promise<typeof Chart>}
 */
export async function getChart() {
  return await loadChart()
}

/**
 * 同步获取 Chart（如果已加载）
 * @returns {typeof Chart|null}
 */
export function getChartSync() {
  return Chart
}

